import { Loader } from "@googlemaps/js-api-loader";
import type { IBasicMap } from "./BasicMap";
import type { IGeocodeResult } from "./Types/IGeocodeResult";

export function useGoogle()
{
    return new Google();
}

export class Google
{

    loader: Loader;

    constructor()
    {
        this.loader = new Loader( {
            apiKey: import.meta.env.VITE_GOOGLE_GEOCODER_API_KEY,
            version: "weekly",
        } );
    }

    /**
     * Creates a basic map with the given options
     * @param mapId 
     * @param options 
     * @returns 
     */
    async createMap( mapId: string, options: IBasicMap )
    {

        return this.loader
            .importLibrary( 'maps' )
            .then( async ( { Map }, ) =>
            {
                
                const { center, zoom, showMarker } = options;
                let position: { lat: number, lng: number };

                // Its a string address so we need to geocode it
                if( typeof center == 'string' )
                {
                    const geocodeResult = await this.getCoordinatesForAddress( center );
                    if( geocodeResult.success == false ) return null;
                    position = { lat: geocodeResult.lat, lng: geocodeResult.lng };
                }

                // Otherwise its a location
                else
                {
                    position = center;    
                }
                
                // Create the map config
                const config = {
                    zoom: zoom,
                    center: position,
                    mapId: mapId,
                    fullscreenControl: false,
                    mapTypeControlOptions: { mapTypeIds: [] }
                };

                // Create the map
                const map = new Map( document.getElementById( mapId ) as HTMLElement, config );
                if( showMarker == false ) return map;
                
                // Create a center location marker if required
                const { AdvancedMarkerElement } = await this.loader.importLibrary( 'marker' );
                const marker = new AdvancedMarkerElement( {
                    map: map,
                    position: position,
                    title: 'Your Location'
                } );

                return map;
                
            } );

    }

    /**
     * Attempts to get the coordinates for an address
     * @param address 
     * @returns 
     */
    async getCoordinatesForAddress( address: string ): Promise<IGeocodeResult>
    {

        const { Geocoder } = await this.loader.importLibrary( 'geocoding' );
        const geocoder = new Geocoder();
        const result = await geocoder.geocode( { 'address': address } );

        if( result == null || result.results.length == 0 ) return { success: false, lat: 0, lng: 0 };
        const coords = result.results[ 0 ].geometry.location;

        return { success: true, lat: coords.lat(), lng: coords.lng() };

    }

}
